import {
    CheckOutlined,
    MailOutlined,
    SearchOutlined,
    StopOutlined,
    UploadOutlined
} from '@ant-design/icons';
import { Button, Card, Input, Layout, Popconfirm, Progress, Select, Space, Table, Tag, theme, Tooltip, Typography } from 'antd';
import { collection, doc as firestoreDoc, getDocs, query, serverTimestamp, updateDoc, writeBatch } from "firebase/firestore";
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as xlsx from 'xlsx';
import apis from '../../apis';
import { db } from '../../firebase';

const { Content } = Layout;
const { Title, Text } = Typography;

/**
 * Parses the "Source" column from the CSV to extract event IDs.
 * Examples:
 *   "2022"                  -> ["APCS2022"]
 *   "2022, 2023 CF"         -> ["APCS2022", "APCS2023"]
 *   "2023 CF, 2023 CW, 2024" -> ["APCS2023", "APCS2024"]
 */
const parseSourceToEventIds = (source) => {
    if (!source || typeof source !== 'string') return [];

    const parts = source.split(',').map(s => s.trim());
    const eventIdSet = new Set();

    parts.forEach(part => {
        // Extract the year (first 4 digits)
        const yearMatch = part.match(/^(\d{4})/);
        if (yearMatch) {
            const year = yearMatch[1];
            eventIdSet.add(`APCS${year}`);
        }
    });

    return Array.from(eventIdSet);
};

const PastRegistrantDashboard = () => {
    const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

    // --- State ---
    const [pastRegistrants, setPastRegistrants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sourceFilter, setSourceFilter] = useState(null);

    // Import states
    const [isImporting, setIsImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importStatus, setImportStatus] = useState('');
    const fileInputRef = useRef(null);

    // Email states
    const [isSendingEmails, setIsSendingEmails] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // --- Fetch data from Firebase on mount ---
    const fetchPastRegistrants = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, "Registrants2025"));
            const snapshot = await getDocs(q);
            const allDocs = snapshot.docs
                .map(doc => ({ ...doc.data(), id: doc.id }))
            // .filter(doc => doc.source === 'csv_import');

            setPastRegistrants(allDocs);
        } catch (err) {
            console.error("Error fetching past registrants:", err);
            toast.error("Failed to fetch past registrant data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPastRegistrants();
    }, []);

    // --- Grouping by Email ---
    const uniqueRegistrants = useMemo(() => {
        const emailMap = new Map();

        pastRegistrants.forEach(record => {
            const emails = (record.performers && record.performers.length > 0)
                ? record.performers.map(p => (p.email || '').toLowerCase().trim()).filter(Boolean)
                : [(record.email || '').toLowerCase().trim()].filter(Boolean);

            emails.forEach(email => {
                if (!emailMap.has(email)) {
                    emailMap.set(email, {
                        id: record.id,
                        primaryName: record.name || '',
                        primaryTeacher: record.teacherName || '',
                        email: email,
                        isBlacklisted: record.isBlacklisted || false,
                        pastEventIds: new Set(record.pastEventIds || []),
                        allNames: new Set([record.name].filter(Boolean)),
                        allTeachers: new Set([record.teacherName].filter(Boolean)),
                        originalRecords: [record]
                    });
                } else {
                    const existing = emailMap.get(email);
                    if (record.name) existing.allNames.add(record.name);
                    if (record.teacherName) existing.allTeachers.add(record.teacherName);
                    if (record.isBlacklisted) existing.isBlacklisted = true;
                    (record.pastEventIds || []).forEach(id => existing.pastEventIds.add(id));
                    existing.originalRecords.push(record);
                }
            });
        });

        return Array.from(emailMap.values()).map(group => ({
            ...group,
            pastEventIds: Array.from(group.pastEventIds).sort(),
            allNamesArray: Array.from(group.allNames),
            allTeachersArray: Array.from(group.allTeachers)
        }));
    }, [pastRegistrants]);

    // --- CSV Import Logic ---
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
            toast.error("Please select a CSV or Excel file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = xlsx.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const csvData = xlsx.utils.sheet_to_json(firstSheet, { defval: '' });

                if (!csvData || csvData.length === 0) {
                    toast.warn("File is empty or could not be parsed.");
                    return;
                }

                toast.info(`Parsed ${csvData.length} rows from file. Starting import...`);
                await importToFirebase(csvData);
            } catch (error) {
                console.error("File parse error:", error);
                toast.error("Failed to parse file.");
            }
        };
        reader.readAsArrayBuffer(file);

        // Reset file input so the same file can be re-selected
        e.target.value = '';
    };

    const importToFirebase = async (csvData) => {
        setIsImporting(true);
        setImportProgress(0);
        setImportStatus('Checking for existing emails...');

        try {
            // 1. Fetch ALL existing emails in Registrants2025 to check for duplicates
            const existingSnapshot = await getDocs(collection(db, "Registrants2025"));
            const existingEmails = new Set();
            existingSnapshot.docs.forEach(doc => {
                const data = doc.data();
                const email = (data.email || '').toLowerCase().trim();
                if (email) existingEmails.add(email);

                // Also check performer emails
                if (data.performers && Array.isArray(data.performers)) {
                    data.performers.forEach(p => {
                        const pEmail = (p.email || '').toLowerCase().trim();
                        if (pEmail) existingEmails.add(pEmail);
                    });
                }
            });

            setImportStatus(`Found ${existingEmails.size} existing emails. Processing CSV rows...`);

            // 2. Filter and prepare documents
            const docsToImport = [];
            const skippedEmails = [];
            const seenEmails = new Set(); // Track emails within the CSV to avoid intra-CSV duplicates

            csvData.forEach(row => {
                const name = String(row['Nama'] || row['Name'] || '').trim();
                const email = String(row['Email'] || '').trim();
                const teacher = String(row['Teacher'] || '').trim();
                const source = String(row['Source'] || '').trim();

                if (!email) return; // Skip rows without email

                const emailLower = email.toLowerCase().trim();

                // Skip if already exists in Firebase or already seen in this CSV
                if (existingEmails.has(emailLower) || seenEmails.has(emailLower)) {
                    skippedEmails.push({ name, email, reason: existingEmails.has(emailLower) ? 'Already in Firebase' : 'Duplicate in CSV' });
                    return;
                }

                seenEmails.add(emailLower);

                const eventIds = parseSourceToEventIds(source);
                const latestEventId = eventIds.length > 0 ? eventIds[eventIds.length - 1] : '';

                docsToImport.push({
                    name: name,
                    email: email,
                    teacherName: teacher,
                    eventId: latestEventId,
                    pastEventIds: eventIds,
                    source: 'csv_import',
                    sourceRaw: source,
                    performers: [{
                        firstName: name,
                        lastName: '',
                        fullName: name,
                        email: email,
                    }],
                    createdAt: serverTimestamp(),
                    PerformanceCategory: '',
                    competitionCategory: '',
                    ageCategory: '',
                    paymentStatus: 'N/A',
                });
            });

            if (skippedEmails.length > 0) {
                toast.info(`Skipped ${skippedEmails.length} duplicate/existing emails.`);
                console.log("Skipped emails:", skippedEmails);
            }

            if (docsToImport.length === 0) {
                toast.warn("No new records to import. All emails already exist.");
                setIsImporting(false);
                return;
            }

            setImportStatus(`Importing ${docsToImport.length} new records...`);

            // 3. Write in batches of 500 (Firestore limit)
            const BATCH_SIZE = 500;
            let imported = 0;

            for (let i = 0; i < docsToImport.length; i += BATCH_SIZE) {
                const batch = writeBatch(db);
                const chunk = docsToImport.slice(i, i + BATCH_SIZE);

                chunk.forEach(docData => {
                    const colRef = collection(db, "Registrants2025");
                    const newDocRef = firestoreDoc(colRef);
                    batch.set(newDocRef, docData);
                });

                await batch.commit();
                imported += chunk.length;
                setImportProgress(Math.round((imported / docsToImport.length) * 100));
                setImportStatus(`Imported ${imported} of ${docsToImport.length} records...`);
            }

            toast.success(`Successfully imported ${docsToImport.length} past registrants!`);
            setImportStatus(`Done! Imported ${docsToImport.length} records. Skipped ${skippedEmails.length}.`);

            // Refresh data
            await fetchPastRegistrants();

        } catch (error) {
            console.error("Import error:", error);
            toast.error(`Import failed: ${error.message || 'Unknown error'}`);
            setImportStatus(`Error: ${error.message}`);
        } finally {
            setIsImporting(false);
        }
    };

    // --- Blacklist Toggle ---
    const handleToggleBlacklist = async (groupedRecord) => {
        const newStatus = !groupedRecord.isBlacklisted;
        try {
            const promises = groupedRecord.originalRecords.map(r => {
                const docRef = firestoreDoc(db, "Registrants2025", r.id);
                return updateDoc(docRef, { isBlacklisted: newStatus });
            });
            await Promise.all(promises);

            // Update local state immediately
            setPastRegistrants(prev =>
                prev.map(r => {
                    if (groupedRecord.originalRecords.some(orig => orig.id === r.id)) {
                        return { ...r, isBlacklisted: newStatus };
                    }
                    return r;
                })
            );

            toast.success(`${groupedRecord.email} ${newStatus ? 'blacklisted' : 'removed from blacklist'}.`);
        } catch (error) {
            console.error("Blacklist toggle error:", error);
            toast.error(`Failed to update blacklist status: ${error.message}`);
        }
    };

    // --- Send Email Logic ---
    const handleSendEmails = async () => {
        const nonBlacklisted = uniqueRegistrants.filter(r => !r.isBlacklisted);

        const dataToSend = selectedRowKeys.length > 0
            ? nonBlacklisted.filter(r => selectedRowKeys.includes(r.email))
            : nonBlacklisted;

        if (!dataToSend || dataToSend.length === 0) {
            toast.error("No data available to send emails.");
            return;
        }

        const uniqueRecipients = dataToSend.map(r => ({
            name: r.primaryName,
            email: r.email
        }));

        const blacklistedCount = uniqueRegistrants.filter(r => r.isBlacklisted).length;
        const selectionNote = selectedRowKeys.length > 0
            ? `This will send to ${uniqueRecipients.length} selected emails (${blacklistedCount} blacklisted excluded).`
            : `This will send to ALL ${uniqueRecipients.length} active registrant emails (${blacklistedCount} blacklisted excluded).`;

        const confirmed = window.confirm(
            `${selectionNote}\n\nDuplicates have been grouped. Are you sure you want to proceed?`
        );
        if (!confirmed) return;

        setIsSendingEmails(true);
        try {
            toast.info(`Sending invite emails to ${uniqueRecipients.length} past registrants...`);
            await apis.email.sendEmailSoundOfAsia2026Invite({ registrants: uniqueRecipients });
            toast.success(`Successfully enqueued invite emails to ${uniqueRecipients.length} past registrants!`);
            setSelectedRowKeys([]);
        } catch (error) {
            console.error(error);
            toast.error(`Failed to send emails: ${error.message || 'Unknown error'}`);
        } finally {
            setIsSendingEmails(false);
        }
    };

    const handleSendTestEmail = async () => {
        const payload = {
            registrants: [
                { name: "Renaldo Louis (Test)", email: "renaldolouis555@gmail.com" },
                { name: "Jefriandy (Test)", email: "jefriandy2@gmail.com" }
            ]
        };

        try {
            toast.info("Sending test Sound of Asia 2026 invite email...");
            await apis.email.sendEmailSoundOfAsia2026Invite(payload);
            toast.success("Successfully sent test email!");
        } catch (error) {
            console.error(error);
            toast.error(`Failed to send test email: ${error.message || 'Unknown error'}`);
        }
    };

    // --- Filtering ---
    const filteredData = uniqueRegistrants.filter(r => {
        let matchSearch = true;
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            matchSearch = r.email.toLowerCase().includes(lower)
                || r.allNamesArray.some(name => name.toLowerCase().includes(lower))
                || r.allTeachersArray.some(teacher => teacher.toLowerCase().includes(lower));
        }

        let matchSource = true;
        if (sourceFilter) {
            matchSource = (r.pastEventIds || []).includes(sourceFilter);
        }

        return matchSearch && matchSource;
    });

    // --- Get unique source options ---
    const sourceOptions = (() => {
        const allEventIds = new Set();
        uniqueRegistrants.forEach(r => {
            (r.pastEventIds || []).forEach(id => allEventIds.add(id));
        });
        return Array.from(allEventIds).sort().map(id => ({ value: id, label: id }));
    })();

    // --- Table columns ---
    const columns = [
        {
            title: '#',
            key: 'index',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Name',
            dataIndex: 'primaryName',
            key: 'name',
            sorter: (a, b) => (a.primaryName || '').localeCompare(b.primaryName || ''),
            render: (_, record) => {
                const { primaryName, allNamesArray } = record;
                const otherNames = allNamesArray.filter(n => n !== primaryName);
                if (otherNames.length > 0) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{primaryName || '-'}</span>
                            <span style={{ fontSize: '12px', color: 'gray' }}>
                                Others: {otherNames.join(', ')}
                            </span>
                        </div>
                    );
                }
                return primaryName || '-';
            },
        },
        {
            title: 'Teacher',
            dataIndex: 'primaryTeacher',
            key: 'teacherName',
            sorter: (a, b) => (a.primaryTeacher || '').localeCompare(b.primaryTeacher || ''),
            render: (_, record) => {
                const { primaryTeacher, allTeachersArray } = record;
                const otherTeachers = allTeachersArray.filter(t => t !== primaryTeacher);
                if (otherTeachers.length > 0) {
                    return (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{primaryTeacher || '-'}</span>
                            <span style={{ fontSize: '12px', color: 'gray' }}>
                                Others: {otherTeachers.join(', ')}
                            </span>
                        </div>
                    );
                }
                return primaryTeacher || '-';
            },
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => a.email.localeCompare(b.email),
            render: (email) => email || '-',
        },
        {
            title: 'Events',
            key: 'pastEventIds',
            render: (_, record) => {
                const ids = record.pastEventIds || [];
                return ids.map(id => {
                    let color = 'blue';
                    if (id.includes('2022')) color = 'green';
                    if (id.includes('2023')) color = 'orange';
                    if (id.includes('2024')) color = 'purple';
                    return <Tag key={id} color={color}>{id}</Tag>;
                });
            },
            filters: [
                { text: 'APCS2022', value: 'APCS2022' },
                { text: 'APCS2023', value: 'APCS2023' },
                { text: 'APCS2024', value: 'APCS2024' },
            ],
            onFilter: (value, record) => (record.pastEventIds || []).includes(value),
        },
        {
            title: 'Status',
            key: 'blacklist',
            width: 120,
            filters: [
                { text: 'Blacklisted', value: true },
                { text: 'Active', value: false },
            ],
            onFilter: (value, record) => value ? !!record.isBlacklisted : !record.isBlacklisted,
            render: (_, record) => {
                const isBlacklisted = !!record.isBlacklisted;
                return (
                    <Popconfirm
                        title={isBlacklisted ? 'Remove from blacklist?' : 'Blacklist this email? (Competitor)'}
                        description={isBlacklisted
                            ? 'This email will receive emails again when you send.'
                            : 'This email will be excluded from all email sends.'}
                        onConfirm={() => handleToggleBlacklist(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title={isBlacklisted ? 'Click to unblock' : 'Click to blacklist (competitor)'}>
                            <Tag
                                color={isBlacklisted ? 'red' : 'green'}
                                style={{ cursor: 'pointer' }}
                                icon={isBlacklisted ? <StopOutlined /> : <CheckOutlined />}
                            >
                                {isBlacklisted ? 'Blocked' : 'Active'}
                            </Tag>
                        </Tooltip>
                    </Popconfirm>
                );
            },
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        preserveSelectedRowKeys: true,
    };

    return (
        <Content
            style={{
                margin: '16px',
            }}
        >
            <div
                style={{
                    padding: 24,
                    minHeight: 360,
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <Title level={3}>Past Registrants (CSV Import)</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
                    Import past event registrants from CSV and send Sound of Asia 2026 invite emails.
                </Text>

                {/* --- Import Section --- */}
                <Card
                    title="Import CSV Data"
                    size="small"
                    style={{ marginBottom: 24 }}
                    extra={
                        <Text type="secondary">
                            {pastRegistrants.length} records loaded ({uniqueRegistrants.length} unique emails)
                        </Text>
                    }
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space wrap>
                            <input
                                type="file"
                                accept=".csv,.xlsx"
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                onChange={handleFileSelect}
                            />
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                loading={isImporting}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Upload CSV & Import to Firebase
                            </Button>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Expected columns: Nama, Email, Teacher, Source (CSV or XLSX)
                            </Text>
                        </Space>

                        {isImporting && (
                            <div style={{ maxWidth: 400 }}>
                                <Progress percent={importProgress} status="active" strokeColor="#e5cc92" />
                                <Text type="secondary" style={{ fontSize: 12 }}>{importStatus}</Text>
                            </div>
                        )}

                        {!isImporting && importStatus && (
                            <Text type="success" style={{ fontSize: 12 }}>{importStatus}</Text>
                        )}
                    </Space>
                </Card>

                {/* --- Email Section --- */}
                <Card
                    title="Send Sound of Asia 2026 Invite"
                    size="small"
                    style={{ marginBottom: 24 }}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space wrap>
                            <Button
                                type="primary"
                                icon={<MailOutlined />}
                                loading={isSendingEmails}
                                onClick={handleSendEmails}
                                style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                            >
                                {selectedRowKeys.length > 0
                                    ? `Send to ${selectedRowKeys.length} Selected`
                                    : `Send to All ${uniqueRegistrants.filter(r => !r.isBlacklisted).length} Active Registrants`}
                            </Button>
                            <Button
                                onClick={handleSendTestEmail}
                                title="Send test Sound of Asia 2026 invite to test emails"
                            >
                                🧪 Send Test Email
                            </Button>
                            {selectedRowKeys.length > 0 && (
                                <Button onClick={() => setSelectedRowKeys([])}>
                                    Clear Selection
                                </Button>
                            )}
                        </Space>
                        {uniqueRegistrants.filter(r => r.isBlacklisted).length > 0 && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <StopOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
                                {uniqueRegistrants.filter(r => r.isBlacklisted).length} competitor(s) blacklisted — will be excluded from email sends.
                            </Text>
                        )}
                    </Space>
                </Card>

                {/* --- Filters --- */}
                <Space style={{ marginBottom: 16 }} wrap>
                    <Input
                        placeholder="Search by name, email, or teacher..."
                        prefix={<SearchOutlined />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                        style={{ width: 300 }}
                    />
                    <Select
                        placeholder="Filter by Event"
                        value={sourceFilter}
                        onChange={setSourceFilter}
                        options={sourceOptions}
                        style={{ width: 180 }}
                        allowClear
                    />
                    <Text type="secondary">
                        Showing {filteredData.length} of {uniqueRegistrants.length} unique records
                    </Text>
                </Space>

                {/* --- Data Table --- */}
                <Table
                    rowSelection={rowSelection}
                    rowKey="email"
                    dataSource={filteredData}
                    columns={columns}
                    loading={loading}
                    pagination={{
                        pageSize: 15,
                        showSizeChanger: true,
                        pageSizeOptions: ['15', '30', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                    }}
                    size="small"
                    scroll={{ x: 800 }}
                />
            </div>
        </Content>
    );
};

export default PastRegistrantDashboard;
